import React, { useState, useEffect } from 'react';
import orbUrl from '../orbAsset';

const wordmarkUrl = "/hypseya-wordmark.png";

export default function LandingPage({ onLogin, onSignUp }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #fafbfc 0%, #f0f7f6 35%, #e8f3f1 60%, #f5f8f7 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '60px 24px 48px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      overflow: 'hidden',
    }}>

      {/* TOP — Orb + Wordmark */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        opacity: ready ? 1 : 0,
        transform: ready ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.9s ease, transform 0.9s ease',
      }}>
        {/* Orb */}
        <div style={{ position: 'relative', width: 'min(160px, 40vw)', height: 'min(160px, 40vw)', marginBottom: 24 }}>
          <img
            src={orbUrl}
            alt="HYPSEYA"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>

        {/* Wordmark with glow backdrop */}
        <div style={{ position: 'relative', marginBottom: 32, display: 'inline-block' }}>
          <div style={{
            position: 'absolute',
            inset: '-12px -20px',
            borderRadius: 40,
            background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.92) 0%, rgba(240,247,246,0.6) 55%, transparent 100%)',
            filter: 'blur(8px)',
            pointerEvents: 'none',
          }} />
          <img
            src={wordmarkUrl}
            alt="HYPSEYA"
            style={{
              width: 'min(240px, 60vw)',
              height: 'auto',
              position: 'relative',
              zIndex: 1,
              filter: 'drop-shadow(0 1px 12px rgba(30,107,104,0.18))',
            }}
          />
        </div>
      </div>

      {/* MIDDLE — Tagline + features */}
      <div style={{
        textAlign: 'center',
        maxWidth: 360,
        opacity: ready ? 1 : 0,
        transform: ready ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 1.1s ease 0.2s, transform 1.1s ease 0.2s',
      }}>
        <p style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.25rem, 5vw, 1.55rem)',
          color: '#1E6B68',
          fontWeight: 400,
          fontStyle: 'italic',
          lineHeight: 1.4,
          margin: '0 0 16px',
        }}>
          For people doing<br />the deeper work.
        </p>
        <p style={{
          fontSize: '0.9rem',
          color: '#7A9E9B',
          lineHeight: 1.7,
          margin: '0 0 40px',
        }}>
          Daily emotional training that builds<br />
          regulation, stability and presence — over time.
        </p>

        {/* Feature pills */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 48 }}>
          {['Daily Loop', 'ERI Score', 'Voice Lab', 'AI Meditations'].map(f => (
            <span key={f} style={{
              background: 'rgba(30,107,104,0.08)',
              color: '#1E6B68',
              borderRadius: 20,
              padding: '5px 14px',
              fontSize: '0.78rem',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}>{f}</span>
          ))}
        </div>
      </div>

      {/* BOTTOM — CTA Buttons */}
      <div style={{
        width: '100%',
        maxWidth: 360,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        opacity: ready ? 1 : 0,
        transform: ready ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 1.2s ease 0.4s, transform 1.2s ease 0.4s',
      }}>
        {/* Primary CTA */}
        <button
          onClick={onSignUp}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 28,
            fontSize: '1rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(30,107,104,0.25)',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          Begin your journey
        </button>

        {/* Secondary — Login */}
        <button
          onClick={onLogin}
          style={{
            width: '100%',
            padding: '15px 24px',
            background: 'transparent',
            color: '#1E6B68',
            border: '1.5px solid rgba(30,107,104,0.35)',
            borderRadius: 28,
            fontSize: '1rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background 0.2s, border-color 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(30,107,104,0.06)'; e.currentTarget.style.borderColor = 'rgba(30,107,104,0.5)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(30,107,104,0.35)'; }}
        >
          I already have an account
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.72rem', color: '#aaa', margin: '8px 0 0' }}>
          By continuing, you accept our Terms and Privacy Policy.
        </p>
      </div>

      <style>{`
        @keyframes hyp-pulse-ring {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.04); }
        }
      `}</style>
    </div>
  );
}
